import React, { useState, useEffect, useRef } from 'react';
import { ShieldCheck, Sparkles, Lock, ArrowRight, User, Phone, Check } from 'lucide-react';
import { Modal } from './ui/Modal.jsx';
import { Button } from './ui/Button.jsx';
import { Badge } from './ui/Badge.jsx';

export function GoogleGIcon({ size = 20 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" style={{ display: 'inline-block', verticalAlign: 'middle' }}>
      <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
      <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
      <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
      <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
      <path fill="none" d="M0 0h48v48H0z"/>
    </svg>
  );
}

export function CloudflareLogo() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11px', fontWeight: 800, color: '#f38020', letterSpacing: '0.5px' }}>
      <svg width="18" height="12" viewBox="0 0 48 32" fill="#f38020">
        <path d="M38.8 14.2c-.8-5.2-5.3-9.2-10.8-9.2-4.1 0-7.8 2.2-9.7 5.6C17.4 10.2 16.2 10 15 10c-5.5 0-10 4.5-10 10 0 .5 0 1 .1 1.5C2.1 22.4 0 25 0 28c0 4.4 3.6 8 8 8h30c5.5 0 10-4.5 10-10 0-4.8-3.4-8.8-8-9.8z"/>
      </svg>
      <span>CLOUDFLARE</span>
    </div>
  );
}

export function AuthModal({
  isOpen,
  onClose,
  targetAsset = null,
  onAuthenticated = null,
  initialTitle = 'Sign in to BizAIPro',
  initialSubtitle = 'Access your digital purchases, commercial license keys, 500+ free vault assets, and AI tools.'
}) {
  const [mode, setMode] = useState('google'); // 'google' | 'register'
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [telephone, setTelephone] = useState('');
  const [agreePrivacy, setAgreePrivacy] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMember, setSuccessMember] = useState(null);
  const [googleClientId, setGoogleClientId] = useState('67674322587-en2irced83a0h68iiq6qoksi0anp7qr5.apps.googleusercontent.com');
  const [savedAccounts, setSavedAccounts] = useState([]);

  const googleBtnContainerRef = useRef(null);

  // 1. Fetch Google Client ID & initialize GSI
  useEffect(() => {
    if (!isOpen) return;
    setError('');
    setSuccessMember(null);

    // Only load saved member if it was verified with Google
    try {
      const saved = localStorage.getItem('bizaipro_member');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed?.email && (parsed.isGoogleVerified || parsed.googleId)) {
          setSavedAccounts([parsed]);
        } else {
          setSavedAccounts([]);
        }
      }
    } catch {
      setSavedAccounts([]);
    }

    fetch('/api/auth/google/config')
      .then((r) => r.json())
      .then((data) => {
        const clientId = data?.clientId || '67674322587-en2irced83a0h68iiq6qoksi0anp7qr5.apps.googleusercontent.com';
        setGoogleClientId(clientId);
        loadGoogleGsiScript(clientId);
      })
      .catch(() => {
        loadGoogleGsiScript('67674322587-en2irced83a0h68iiq6qoksi0anp7qr5.apps.googleusercontent.com');
      });
  }, [isOpen]);

  const loadGoogleGsiScript = (clientId) => {
    if (window.google?.accounts?.id) {
      initGsi(clientId);
      return;
    }
    const existing = document.getElementById('google-gsi-script');
    if (existing) {
      existing.onload = () => initGsi(clientId);
      return;
    }
    const script = document.createElement('script');
    script.id = 'google-gsi-script';
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => initGsi(clientId);
    document.head.appendChild(script);
  };

  const initGsi = (clientId) => {
    if (!window.google?.accounts?.id || !clientId) return;
    try {
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleGoogleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      setTimeout(() => {
        if (googleBtnContainerRef.current) {
          googleBtnContainerRef.current.innerHTML = '';
          window.google.accounts.id.renderButton(googleBtnContainerRef.current, {
            type: 'standard',
            theme: 'filled_blue',
            size: 'large',
            text: 'continue_with',
            shape: 'pill',
            logo_alignment: 'left',
            width: 320,
          });
        }
      }, 50);
    } catch (err) {
      console.warn('GSI init notice:', err);
    }
  };

  // Verifies the official Google ID token with our backend
  const handleGoogleCredentialResponse = async (response) => {
    if (!response?.credential) return;
    setLoading(true);
    setError('');

    try {
      const res = await fetch('/api/auth/google/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          credential: response.credential,
          slug: targetAsset?.slug || 'auth-modal',
          telephone: telephone.trim() || undefined,
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || 'Google verification failed. Only authentic Google accounts are accepted.');
      }

      finishAuth(data.member);
    } catch (err) {
      setError(err.message || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Triggers official Google OAuth2 popup
  const triggerGoogleOAuth = (loginHint = '') => {
    setError('');
    const effectiveClientId = googleClientId || '67674322587-en2irced83a0h68iiq6qoksi0anp7qr5.apps.googleusercontent.com';

    // 1. Official Google OAuth2 Popup client
    if (window.google?.accounts?.oauth2) {
      try {
        setLoading(true);
        const tokenClient = window.google.accounts.oauth2.initTokenClient({
          client_id: effectiveClientId,
          scope: 'email profile openid',
          hint: loginHint || undefined,
          callback: async (tokenResponse) => {
            if (tokenResponse?.error) {
              setLoading(false);
              if (tokenResponse.error !== 'popup_closed_by_user') {
                setError(`Google sign-in was not completed: ${tokenResponse.error}`);
              }
              return;
            }
            if (tokenResponse?.access_token) {
              try {
                const res = await fetch('/api/auth/google/verify', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    accessToken: tokenResponse.access_token,
                    slug: targetAsset?.slug || 'auth-modal',
                    telephone: telephone.trim() || undefined,
                  }),
                });
                const data = await res.json();
                if (!res.ok || !data.ok) {
                  throw new Error(data.error || 'Google verification failed.');
                }
                finishAuth(data.member);
              } catch (err) {
                setError(err.message || 'Verification failed. Please try again.');
              } finally {
                setLoading(false);
              }
            } else {
              setLoading(false);
            }
          },
        });
        tokenClient.requestAccessToken({ prompt: loginHint ? undefined : 'select_account' });
        return;
      } catch (err) {
        console.warn('OAuth2 popup client error:', err);
        setLoading(false);
      }
    }

    // 2. Fallback to GSI One-Tap prompt
    if (window.google?.accounts?.id) {
      try {
        window.google.accounts.id.prompt((notification) => {
          if (notification.isNotDisplayed()) {
            setError('Please use the Google Sign-In button or allow popups for accounts.google.com.');
          }
        });
        return;
      } catch (e) {
        console.warn('GSI prompt notice:', e);
      }
    }

    setError('Google Identity Services is loading. Please try again in a few moments.');
  };

  const handleRegisterWithGoogle = (e) => {
    if (e) e.preventDefault();
    if (!agreePrivacy) {
      setError('You must agree to the Privacy Policy to proceed.');
      return;
    }
    triggerGoogleOAuth();
  };

  const finishAuth = (member) => {
    try {
      localStorage.setItem('bizaipro_member', JSON.stringify(member));
      window.dispatchEvent(new CustomEvent('bizaipro-auth-change', { detail: member }));
    } catch {}

    setSuccessMember(member);
    if (onAuthenticated) {
      onAuthenticated(member, targetAsset);
    }

    setTimeout(() => {
      onClose();
      // If target is a free asset, trigger download
      if (targetAsset && targetAsset.slug && !targetAsset.priceBDT && !targetAsset.priceUSD) {
        window.location.href = `/api/downloads/${targetAsset.slug}?email=${encodeURIComponent(member.email)}`;
      }
    }, 850);
  };

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      className="bza-auth-modal"
    >
      {successMember ? (
        <div style={{ textAlign: 'center', padding: '24px 0' }}>
          <div style={{ display: 'inline-flex', marginBottom: '16px' }}>
            {successMember.picture ? (
              <img
                src={successMember.picture}
                alt={successMember.name}
                style={{ width: '64px', height: '64px', borderRadius: '50%', border: '2px solid var(--success-base)' }}
                referrerPolicy="no-referrer"
              />
            ) : (
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#21499b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 26, fontWeight: 700 }}>
                {(successMember.name || successMember.email || 'G').charAt(0).toUpperCase()}
              </div>
            )}
          </div>
          <h2 style={{ fontSize: '22px', fontWeight: 700, margin: '0 0 8px', color: 'var(--text-primary)' }}>
            Welcome, {successMember.name || successMember.email}!
          </h2>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', margin: '0 auto 16px' }}>
            <Badge variant="success" dot>Official Google Verified</Badge>
          </div>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', margin: '0 0 20px' }}>
            Your Google identity is authentic and verified. Unlocking your member access...
          </p>
          {targetAsset && (
            <div style={{ background: 'var(--bg-subtle)', padding: '12px 16px', borderRadius: 'var(--radius-sm)', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
              <Sparkles size={16} color="var(--accent-base)" />
              <span style={{ fontSize: '13px', fontWeight: 600 }}>Unlocking {targetAsset.title || targetAsset.name}...</span>
            </div>
          )}
        </div>
      ) : mode === 'google' ? (
        /* MODE 1: Google Account Sign-In */
        <div className="google-auth-view">
          <div style={{ textAlign: 'center', marginBottom: '22px' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
              <GoogleGIcon size={28} />
              <span style={{ fontSize: '17px', fontWeight: 700, color: 'var(--text-primary)', letterSpacing: '-0.01em' }}>
                Sign in with Google
              </span>
            </div>
            <h2 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 6px', color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              Choose an account
            </h2>
            <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: 0 }}>
              to continue to <strong style={{ color: 'var(--accent-base)' }}>BizAIPro</strong>
            </p>
          </div>

          {targetAsset && (
            <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-default)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', marginBottom: '18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <small style={{ fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 700, color: 'var(--text-muted)' }}>
                  Download Gate
                </small>
                <div style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-primary)' }}>
                  {targetAsset.title || targetAsset.name}
                </div>
              </div>
              <Badge variant="brand" size="sm">Google Auth Required</Badge>
            </div>
          )}

          {error && (
            <div style={{ background: 'var(--danger-surface)', border: '1px solid var(--danger-border)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: '13px', marginBottom: '16px' }} role="alert">
              {error}
            </div>
          )}

          {/* Official GSI Button Container */}
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '14px', minHeight: '44px' }}>
            <div ref={googleBtnContainerRef} />
          </div>

          {/* Saved Verified Google Accounts List */}
          {savedAccounts.length > 0 && (
            <div style={{ border: '1px solid var(--border-default)', borderRadius: '10px', overflow: 'hidden', marginBottom: '14px' }}>
              {savedAccounts.map((acc, idx) => (
                <div
                  key={idx}
                  onClick={() => triggerGoogleOAuth(acc.email)}
                  style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', cursor: 'pointer', borderBottom: idx < savedAccounts.length - 1 ? '1px solid var(--border-subtle)' : 'none', background: 'var(--bg-surface)', transition: 'background 0.15s ease' }}
                  onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-hover)'}
                  onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-surface)'}
                >
                  <div style={{ width: 38, height: 38, borderRadius: '50%', background: '#7c3aed', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16 }}>
                    {(acc.name || acc.email || 'A').charAt(0).toUpperCase()}
                  </div>
                  <div style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                    <div style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)' }}>{acc.name || 'Google Account'}</div>
                    <div style={{ fontSize: '12.5px', color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{acc.email}</div>
                  </div>
                  <Badge variant="success" size="sm">Verified</Badge>
                </div>
              ))}
              <div
                onClick={() => triggerGoogleOAuth()}
                style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '12px 16px', cursor: 'pointer', background: 'var(--bg-surface)', borderTop: '1px solid var(--border-subtle)' }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--bg-surface-hover)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'var(--bg-surface)'}
              >
                <div style={{ width: 38, height: 38, borderRadius: '50%', border: '1px dashed var(--border-default)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
                  <User size={18} />
                </div>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  Use another Google account
                </div>
              </div>
            </div>
          )}

          {/* Primary Branded "Continue with Google" popup button */}
          <div style={{ marginBottom: '14px' }}>
            <button
              type="button"
              onClick={() => triggerGoogleOAuth()}
              disabled={loading}
              style={{ width: '100%', minHeight: '48px', padding: '10px 16px', background: '#fff', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: '9999px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', fontSize: '15px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', transition: 'all 0.15s ease' }}
              onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.12)'; e.currentTarget.style.borderColor = '#9ca3af'; }}
              onMouseLeave={(e) => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.08)'; e.currentTarget.style.borderColor = '#d1d5db'; }}
            >
              <GoogleGIcon size={22} />
              <span>{loading ? 'Verifying with Google...' : 'Continue with Google'}</span>
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', fontSize: '12px', color: 'var(--text-muted)', marginBottom: '12px' }}>
            <ShieldCheck size={14} color="var(--success-base)" />
            <span>Cryptographically Verified via Google Identity Services</span>
          </div>

          {/* Google Disclosure */}
          <p style={{ fontSize: '11.5px', color: 'var(--text-muted)', lineHeight: '1.5', margin: '14px 0 16px', textAlign: 'left' }}>
            To continue, Google will share your verified name, email address, and profile picture with BizAIPro. Before using this app, you can review BizAIPro's{' '}
            <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-base)', textDecoration: 'underline' }}>privacy policy</a>{' '}
            and{' '}
            <a href="/terms-of-use/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-base)', textDecoration: 'underline' }}>terms of service</a>.
          </p>

          {/* Switch to Register Form */}
          <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '14px', textAlign: 'center' }}>
            <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Need a new account? </span>
            <button
              type="button"
              onClick={() => { setMode('register'); setError(''); }}
              style={{ background: 'transparent', border: 'none', color: 'var(--accent-base)', fontWeight: 700, fontSize: '13px', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
            >
              Register with Verified Google ID
            </button>
          </div>
        </div>
      ) : (
        /* MODE 2: Register Account Form */
        <div className="register-auth-view">
          <div style={{ textAlign: 'left', marginBottom: '18px' }}>
            <h2 style={{ fontSize: '24px', fontWeight: 800, margin: '0 0 4px', color: 'var(--text-primary)', letterSpacing: '-0.02em' }}>
              Register Account
            </h2>
            <p style={{ fontSize: '13px', color: 'var(--text-muted)', margin: 0 }}>
              Create your official BizAIPro account verified with Google to manage software licenses and instant downloads.
            </p>
          </div>

          {error && (
            <div style={{ background: 'var(--danger-surface)', border: '1px solid var(--danger-border)', color: 'var(--danger-text)', padding: '10px 14px', borderRadius: 'var(--radius-sm)', fontSize: '13px', marginBottom: '16px' }} role="alert">
              {error}
            </div>
          )}

          <div style={{ background: 'var(--bg-subtle)', border: '1px solid var(--border-default)', borderRadius: '8px', padding: '14px', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <ShieldCheck size={18} color="var(--success-base)" />
              <span style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-primary)' }}>
                Official Google Identity Verification
              </span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--text-muted)', margin: 0, lineHeight: 1.5 }}>
              To prevent fraudulent licenses and fake account creation, all member registrations are securely authenticated through Google. Your authentic Gmail address will be verified automatically.
            </p>
          </div>

          <form onSubmit={handleRegisterWithGoogle}>
            {/* Optional Telephone */}
            <div className="bza-input-field" style={{ marginBottom: '12px' }}>
              <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '4px' }}>
                Telephone / WhatsApp (Optional for Order Updates)
              </label>
              <input
                type="tel"
                placeholder="+880 1xxx-xxxxxx"
                value={telephone}
                onChange={(e) => setTelephone(e.target.value)}
                style={{ width: '100%', height: '42px', padding: '0 12px', border: '1px solid var(--border-default)', borderRadius: '6px', fontSize: '14px', background: 'var(--bg-surface)', color: 'var(--text-primary)', boxSizing: 'border-box' }}
              />
            </div>

            {/* Privacy Policy Checkbox */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '14px 0' }}>
              <input
                type="checkbox"
                id="agree-privacy-chk"
                checked={agreePrivacy}
                onChange={(e) => setAgreePrivacy(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#21499b', cursor: 'pointer' }}
              />
              <label htmlFor="agree-privacy-chk" style={{ fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}>
                I have read and agree to the{' '}
                <a href="/privacy-policy/" target="_blank" rel="noopener noreferrer" style={{ color: '#ea580c', fontWeight: 600 }}>
                  Privacy Policy
                </a>
              </label>
            </div>

            {/* Cloudflare Verification Badge */}
            <div style={{ background: '#222222', color: '#fff', borderRadius: '6px', padding: '12px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Check size={14} color="#fff" strokeWidth={3} />
                </div>
                <span style={{ fontSize: '14px', fontWeight: 700 }}>Security Verified</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <CloudflareLogo />
                <div style={{ fontSize: '9px', color: '#9ca3af', marginTop: '2px' }}>
                  Anti-Abuse Protection
                </div>
              </div>
            </div>

            {/* Verify & Create Account with Google Button */}
            <button
              type="submit"
              disabled={loading}
              style={{ width: '100%', minHeight: '48px', background: '#3b49df', color: '#fff', border: 'none', borderRadius: '6px', fontSize: '15px', fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', transition: 'background 0.15s ease' }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#2f3ab2'}
              onMouseLeave={(e) => e.currentTarget.style.background = '#3b49df'}
            >
              <GoogleGIcon size={20} />
              <span>{loading ? 'Verifying with Google...' : 'Verify & Register with Google'}</span>
            </button>

            {/* Footer toggle back to Google Sign-In */}
            <div style={{ borderTop: '1px solid var(--border-subtle)', paddingTop: '16px', marginTop: '16px', textAlign: 'center' }}>
              <p style={{ margin: '0 0 4px', fontSize: '12.5px', color: 'var(--text-muted)' }}>
                Already have an account?
              </p>
              <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
                Sign in directly using your verified{' '}
                <button
                  type="button"
                  onClick={() => { setMode('google'); setError(''); }}
                  style={{ background: 'transparent', border: 'none', color: '#ea580c', fontWeight: 700, fontSize: '13px', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
                >
                  Google Account
                </button>.
              </div>
            </div>
          </form>
        </div>
      )}
    </Modal>
  );
}

export default AuthModal;
